/**
 * Small render helpers: a reusable Sprite pool (so the dynamic layer doesn't churn the
 * GC at 60fps) and a Label that re-bakes its bitmap-font texture only when its text
 * changes. Positions are rounded to integers so pixels stay aligned under upscale.
 */
import { Container, Sprite, Texture } from 'pixi.js';
import { textTexture } from './pixelfont';

/** Hands out reusable sprites each frame; hides any not claimed. */
export class SpritePool {
  private sprites: Sprite[] = [];
  private idx = 0;
  constructor(private parent: Container) {}

  begin(): void {
    this.idx = 0;
  }

  /** Claim a sprite showing `tex` at (x,y). Resets transform state to sane defaults. */
  put(tex: Texture, x: number, y: number, anchor = 0.5): Sprite {
    let s = this.sprites[this.idx];
    if (!s) {
      s = new Sprite(tex);
      this.parent.addChild(s);
      this.sprites.push(s);
    } else {
      s.texture = tex;
    }
    s.visible = true;
    s.anchor.set(anchor);
    s.position.set(Math.round(x), Math.round(y));
    s.scale.set(1);
    s.rotation = 0;
    s.alpha = 1;
    s.tint = 0xffffff;
    this.idx++;
    return s;
  }

  end(): void {
    for (let i = this.idx; i < this.sprites.length; i++) this.sprites[i]!.visible = false;
  }
}

interface LabelStyle {
  color?: string;
  shadow?: string;
  outline?: string;
  scale?: number;
  anchor?: number;
}

/** A bitmap-font text sprite that only re-bakes its texture when the string changes. */
export class Label {
  readonly sprite = new Sprite();
  private last = ' ';
  private style: Required<Omit<LabelStyle, 'scale' | 'anchor'>>;
  private scale: number;

  constructor(parent: Container, style: LabelStyle = {}) {
    this.style = {
      color: style.color ?? '#ffffff',
      shadow: style.shadow ?? '',
      outline: style.outline ?? '',
    };
    this.scale = style.scale ?? 1;
    this.sprite.anchor.set(style.anchor ?? 0);
    this.sprite.scale.set(this.scale);
    parent.addChild(this.sprite);
  }

  set(text: string): this {
    if (text !== this.last) {
      this.last = text;
      this.sprite.texture = textTexture(text, {
        color: this.style.color,
        shadow: this.style.shadow || undefined,
        outline: this.style.outline || undefined,
      });
    }
    return this;
  }

  at(x: number, y: number): this {
    this.sprite.position.set(Math.round(x), Math.round(y));
    return this;
  }

  setColor(color: string): this {
    if (color !== this.style.color) {
      this.style.color = color;
      this.last = ' '; // force re-bake with new color
    }
    return this;
  }

  set visible(v: boolean) {
    this.sprite.visible = v;
  }
}
